import React from "react";
import { useDocument } from "../loan_tool";
import TextField from "../../fields/text_field";
import PhoneField from "../../fields/phone_field";
import CurrencyField from "../../fields/currency_field";
import PercentField from "../../fields/percent_field";
import NumberField from "../../fields/number_field";

const DEFAULT_PAGE1 = {
  accepted_price: "0",
  estimated_hold_time: "",
  arv: "0",
  annual_insurance: "1200",
  property_tax_rate: "1.25",
  lo_name: "",
  lo_title: "Loan Officer",
  lo_cell: "",
  lo_office: "",
  lo_email: ""
};

function ReadOnlyField({ label, value }) {
  return (
    <div className="ff-read-only-field">
      <div className="ff-field-label">{label}</div>
      <div className="ff-field-value">{value || "—"}</div>
    </div>
  );
}

function formatCurrency(val) {
  const n = parseFloat(val);
  if (!val || isNaN(n) || n === 0) return "—";
  return "$" + n.toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 });
}

export default function FixAndFlipPage1() {
  const [
    document,
    setDocument,
    { documentAccountProfile, lastSaveDate },
    { saveDocument }
  ] = useDocument();

  const { applicant, property, loans } = document;
  const fix_and_flip = document.fix_and_flip || {};
  const page1 = { ...DEFAULT_PAGE1, ...fix_and_flip.page1 };

  const borrowers = applicant.borrowers || [];

  // Sales Price / Offer Price auto-fills from the Down Payment page (loans[0].offer_price)
  const salesPrice = loans && loans[0] ? loans[0].offer_price : "0";

  function updatePage1(updatedFields) {
    setDocument({
      ...document,
      fix_and_flip: {
        ...fix_and_flip,
        page1: { ...page1, ...updatedFields }
      }
    });
  }

  function formatLastSaved() {
    if (!lastSaveDate) return "Not yet saved";
    return "Last saved: " + new Date(lastSaveDate).toLocaleTimeString();
  }

  const loName =
    page1.lo_name ||
    [documentAccountProfile.first_name, documentAccountProfile.last_name]
      .filter(Boolean)
      .join(" ");
  const loCell = page1.lo_cell || documentAccountProfile.cell_phone || "";
  const loOffice = page1.lo_office || documentAccountProfile.office_phone || "";
  const loEmail = page1.lo_email || documentAccountProfile.email || "";

  return (
    <div>
      {/* Borrowers */}
      <div className="ff-section">
        <div className="ff-section-title">Borrowers (auto-filled from Applicant Info)</div>
        <div className="ff-section-body">
          {[0, 1, 2, 3].map(i => {
            const b = borrowers[i];
            const label = i === 0 ? "Borrower 1 (Primary)" : `Borrower ${i + 1}`;
            const value = b
              ? [b.first_name, b.middle_name, b.last_name].filter(Boolean).join(" ")
              : null;
            return <ReadOnlyField key={i} label={label} value={value} />;
          })}
        </div>
      </div>

      {/* Property Info */}
      <div className="ff-section">
        <div className="ff-section-title">Property Information</div>
        <div className="ff-section-body">
          <ReadOnlyField
            label="Property Address"
            value={[property.address, property.address2].filter(Boolean).join(" ")}
          />
          <ReadOnlyField label="City" value={property.city} />
          <ReadOnlyField label="State" value={property.state} />
          <ReadOnlyField label="ZIP" value={property.zip} />
          <ReadOnlyField
            label="List Price (from New Property)"
            value={formatCurrency(property.listed_price)}
          />
          <ReadOnlyField
            label="Sales Price / Offer Price (from Down Payment)"
            value={formatCurrency(salesPrice)}
          />
          <CurrencyField
            name="Accepted Price"
            fieldName="accepted_price"
            fixedDigits={0}
            value={page1.accepted_price}
            onChange={updatePage1}
          />
          <NumberField
            name="Estimated Hold Time (months)"
            fieldName="estimated_hold_time"
            value={page1.estimated_hold_time}
            onChange={updatePage1}
          />
          <div className="ff-arv-wrapper">
            <CurrencyField
              name="After Repair Value (ARV)"
              fieldName="arv"
              fixedDigits={0}
              value={page1.arv}
              onChange={updatePage1}
            />
            <div className="ff-arv-note">(Estimated)</div>
          </div>
          <CurrencyField
            name="Est. Annual Home Insurance"
            fieldName="annual_insurance"
            fixedDigits={0}
            value={page1.annual_insurance}
            onChange={updatePage1}
          />
          <PercentField
            name="Est. Property Tax Rate (%)"
            fieldName="property_tax_rate"
            fixedDigits={2}
            value={page1.property_tax_rate}
            onChange={updatePage1}
          />
        </div>
      </div>

      {/* Loan Officer Info */}
      <div className="ff-section">
        <div className="ff-section-title">Loan Officer Information (auto-filled from your profile)</div>
        <div className="ff-section-body">
          <TextField
            name="Loan Officer Name"
            fieldName="lo_name"
            value={loName}
            onChange={updatedFields =>
              updatePage1({ lo_name: updatedFields.lo_name })
            }
          />
          <TextField
            name="Title"
            fieldName="lo_title"
            value={page1.lo_title}
            onChange={updatePage1}
          />
          <PhoneField
            name="Cell Phone"
            fieldName="lo_cell"
            value={loCell}
            onChange={updatedFields =>
              updatePage1({ lo_cell: updatedFields.lo_cell })
            }
          />
          <PhoneField
            name="Office Phone"
            fieldName="lo_office"
            value={loOffice}
            onChange={updatedFields =>
              updatePage1({ lo_office: updatedFields.lo_office })
            }
          />
          <div className="ff-full-width">
            <TextField
              name="Email"
              fieldName="lo_email"
              value={loEmail}
              onChange={updatedFields =>
                updatePage1({ lo_email: updatedFields.lo_email })
              }
            />
          </div>
          <ReadOnlyField
            label="Personal NMLS #"
            value={documentAccountProfile.nmls_number}
          />
          <ReadOnlyField
            label="CA DRE #"
            value={documentAccountProfile.state_real_estate_license}
          />
        </div>
      </div>

      {/* Footer */}
      <div className="ff-footer">
        <span className="ff-last-saved">{formatLastSaved()}</span>
        <div className="ff-footer-actions">
          <button className="button" onClick={saveDocument}>
            Save to Loan File
          </button>
        </div>
      </div>
    </div>
  );
}
